import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Paperclip } from "lucide-react";
import AdminLayout from "layouts/AdminLayout";
import AdminCard from "components/admin/AdminCard";
import StatusBadge from "components/admin/StatusBadge";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  AdminChamadoDetalhe as ChamadoDetalheDados,
  ChamadoStatus,
  atualizarChamado,
  baixarAnexoChamado,
  getAdminChamado,
  responderChamado,
} from "services/admin/adminChamados.service";
import { ROTULO_STATUS, ROTULO_TIPO, TOM_STATUS } from "./index";

const SELECT_CLASS = "h-9 rounded-md border border-input bg-background px-3 text-sm";

const ROTULO_AUTOR: Record<string, string> = {
  CLIENTE: "Cliente",
  INTREX: "Intrex",
  TRANSPORTADORA: "Transportadora",
};

// A bolha diz de quem é a fala sem precisar ler o rótulo.
const ESTILO_AUTOR: Record<string, string> = {
  CLIENTE: "bg-slate-50 border-slate-200",
  INTREX: "bg-blue-50 border-blue-200",
  TRANSPORTADORA: "bg-amber-50 border-amber-200",
};

const MIMES_ACEITOS = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];

const quando = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("pt-BR") : "—";

const mensagemErro = (err: unknown, padrao: string) =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
  (err as Error)?.message ||
  padrao;

const AdminChamadoDetalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dados, setDados] = useState<ChamadoDetalheDados | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [texto, setTexto] = useState("");
  const [novoStatus, setNovoStatus] = useState<ChamadoStatus | "">("");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const inputArquivo = useRef<HTMLInputElement>(null);

  // Campos de classificação, editáveis: a classificação automática lê o texto
  // da transportadora e chuta tipo, título e prazo — quem atende corrige aqui.
  const [form, setForm] = useState({ tipo: "", titulo: "", descricao: "", acao: "", prazo: "" });

  const carregar = () => {
    if (!id) return;
    setLoading(true);
    getAdminChamado(id)
      .then((d) => {
        setDados(d);
        setForm({
          tipo: d.chamado.tipo,
          titulo: d.chamado.titulo,
          descricao: d.chamado.descricao ?? "",
          acao: d.chamado.acao ?? "",
          prazo: d.chamado.prazo ?? "",
        });
        setErro(null);
      })
      .catch((err) => {
        console.error("[AdminChamadoDetalhe] erro:", err);
        setErro(mensagemErro(err, "Não foi possível carregar o chamado."));
      })
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [id]);

  const lerArquivo = (f: File) =>
    new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(new Error("Falha ao ler o arquivo."));
      r.readAsDataURL(f);
    });

  const enviar = async () => {
    if (!id) return;
    if (!texto.trim() && !arquivo) {
      setErro("Escreva algo ou anexe um arquivo.");
      return;
    }

    setEnviando(true);
    setErro(null);
    try {
      let anexo;
      if (arquivo) {
        if (!MIMES_ACEITOS.includes(arquivo.type)) {
          setErro("Envie PDF, JPG ou PNG.");
          setEnviando(false);
          return;
        }
        const dataUrl = await lerArquivo(arquivo);
        anexo = {
          base64: dataUrl.replace(/^data:.*;base64,/, ""),
          mime: arquivo.type,
          nome: arquivo.name,
        };
      }

      await responderChamado(id, {
        texto: texto.trim() || undefined,
        status: novoStatus || undefined,
        anexo,
      });

      setTexto("");
      setArquivo(null);
      setNovoStatus("");
      if (inputArquivo.current) inputArquivo.current.value = "";
      carregar();
    } catch (err) {
      console.error("[AdminChamadoDetalhe] erro ao responder:", err);
      setErro(mensagemErro(err, "Não foi possível enviar a resposta."));
    } finally {
      setEnviando(false);
    }
  };

  const salvarClassificacao = async () => {
    if (!id) return;
    setSalvando(true);
    setErro(null);
    try {
      await atualizarChamado(id, {
        tipo: form.tipo as never,
        titulo: form.titulo,
        // "" vira null no backend: campo apagado some, não fica string vazia.
        descricao: form.descricao,
        acao: form.acao,
        prazo: form.prazo,
      });
      carregar();
    } catch (err) {
      console.error("[AdminChamadoDetalhe] erro ao salvar:", err);
      setErro(mensagemErro(err, "Não foi possível salvar."));
    } finally {
      setSalvando(false);
    }
  };

  const mudarStatus = async (status: ChamadoStatus) => {
    if (!id) return;
    setSalvando(true);
    setErro(null);
    try {
      await atualizarChamado(id, { status });
      carregar();
    } catch (err) {
      console.error("[AdminChamadoDetalhe] erro ao mudar status:", err);
      setErro(mensagemErro(err, "Não foi possível alterar o status."));
    } finally {
      setSalvando(false);
    }
  };

  if (loading && !dados) {
    return (
      <AdminLayout title="Chamado" subtitle="Atendimento">
        <p className="text-sm text-muted-foreground">Carregando...</p>
      </AdminLayout>
    );
  }

  if (!dados) {
    return (
      <AdminLayout title="Chamado" subtitle="Atendimento">
        <AdminCard>
          <p className="text-sm text-red-600">{erro || "Chamado não encontrado."}</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate("/admin/chamados")}>
            Voltar
          </Button>
        </AdminCard>
      </AdminLayout>
    );
  }

  const { chamado, mensagens, telefone, status_envio, tracking_raw } = dados;

  return (
    <AdminLayout
      title={chamado.titulo}
      subtitle={`Chamado #${chamado.id} · ${chamado.cliente?.razaoSocial ?? "cliente"}`}
      action={
        <div className="flex items-center gap-2">
          <StatusBadge label={ROTULO_STATUS[chamado.status]} tone={TOM_STATUS[chamado.status]} />
          {chamado.status !== "RESOLVIDO" ? (
            <Button size="sm" disabled={salvando} onClick={() => mudarStatus("RESOLVIDO")}>
              Encerrar
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={salvando}
              onClick={() => mudarStatus("EM_ANALISE")}
            >
              Reabrir
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate("/admin/chamados")}>
            Voltar
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {erro ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {erro}
          </div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 flex flex-col gap-5">
            <AdminCard title="Conversa">
              <div className="flex flex-col gap-3">
                {mensagens.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Nenhuma mensagem ainda neste chamado.
                  </p>
                ) : (
                  mensagens.map((m) => (
                    <div
                      key={m.id}
                      className={`rounded-lg border px-4 py-3 ${
                        ESTILO_AUTOR[m.autor] ?? "bg-slate-50 border-slate-200"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <span className="text-[12px] font-semibold text-slate-700">
                          {ROTULO_AUTOR[m.autor] ?? m.autor}
                        </span>
                        <span className="text-[11px] text-slate-500">{quando(m.criado_em)}</span>
                      </div>
                      {m.texto ? (
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{m.texto}</p>
                      ) : null}
                      {m.tem_anexo ? (
                        <button
                          type="button"
                          className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-medium text-blue-700 hover:underline"
                          onClick={() =>
                            baixarAnexoChamado(chamado.id, m.id, m.anexo_nome || "anexo")
                          }
                        >
                          <Paperclip className="w-3.5 h-3.5" />
                          {m.anexo_nome || "anexo"}
                        </button>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </AdminCard>

            <AdminCard title="Responder como Intrex">
              <div className="flex flex-col gap-3">
                <textarea
                  className="min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder="O cliente lê esta mensagem na tela de ocorrências dele."
                />
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    ref={inputArquivo}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="text-sm"
                    onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
                  />
                  <select
                    className={SELECT_CLASS}
                    value={novoStatus}
                    onChange={(e) => setNovoStatus(e.target.value as ChamadoStatus | "")}
                  >
                    <option value="">Manter o status</option>
                    <option value="ACAO_CLIENTE">Passar para o cliente</option>
                    <option value="EM_ANALISE">Manter com a Intrex</option>
                    <option value="RESOLVIDO">Encerrar</option>
                  </select>
                  <Button
                    disabled={enviando}
                    className="bg-brand-sidebar hover:bg-brand-sidebar/90"
                    onClick={enviar}
                  >
                    {enviando ? "Enviando..." : "Enviar resposta"}
                  </Button>
                </div>
                <p className="text-[12px] text-slate-500">
                  Pedindo documento ou informação, escolha “Passar para o cliente”: é o que
                  faz o chamado aparecer como pendência na tela dele.
                </p>
              </div>
            </AdminCard>
          </div>

          <div className="flex flex-col gap-5">
            <AdminCard title="Cliente e envio">
              <dl className="flex flex-col gap-2 text-sm">
                <div>
                  <dt className="text-[11px] uppercase tracking-wide text-slate-500">Cliente</dt>
                  <dd className="text-slate-900">{chamado.cliente?.razaoSocial ?? "—"}</dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-wide text-slate-500">E-mail</dt>
                  <dd className="text-slate-900 break-all">
                    {chamado.cliente?.emailPrincipal ?? "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-wide text-slate-500">Telefone</dt>
                  <dd className="text-slate-900">{telefone || "—"}</dd>
                </div>
                {chamado.envio ? (
                  <>
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-slate-500">Pedido</dt>
                      <dd className="text-slate-900">{chamado.envio.pedido_ref}</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-slate-500">
                        Rastreio
                      </dt>
                      <dd className="text-slate-900">
                        {chamado.envio.carrier} · {chamado.envio.tracking_number || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-slate-500">Destino</dt>
                      <dd className="text-slate-900">{chamado.envio.destino || "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-slate-500">
                        Status do envio
                      </dt>
                      <dd className="text-slate-900">{status_envio || "—"}</dd>
                    </div>
                  </>
                ) : (
                  <p className="text-slate-500">Chamado sem envio vinculado.</p>
                )}
                <div>
                  <dt className="text-[11px] uppercase tracking-wide text-slate-500">Aberto em</dt>
                  <dd className="text-slate-900">{quando(chamado.criado_em)}</dd>
                </div>
                {chamado.resolvido_em ? (
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide text-slate-500">
                      Resolvido em
                    </dt>
                    <dd className="text-slate-900">{quando(chamado.resolvido_em)}</dd>
                  </div>
                ) : null}
              </dl>
            </AdminCard>

            <AdminCard title="Classificação">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label>Tipo</Label>
                  <select
                    className={SELECT_CLASS}
                    value={form.tipo}
                    onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  >
                    {Object.entries(ROTULO_TIPO).map(([valor, rotulo]) => (
                      <option key={valor} value={valor}>
                        {rotulo}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Título</Label>
                  <Input
                    value={form.titulo}
                    onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>O que aconteceu</Label>
                  <textarea
                    className="min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.descricao}
                    onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>O que o cliente precisa fazer</Label>
                  <textarea
                    className="min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.acao}
                    onChange={(e) => setForm({ ...form, acao: e.target.value })}
                    placeholder="Em branco quando a bola está com a Intrex."
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Prazo</Label>
                  <Input
                    type="date"
                    value={form.prazo}
                    onChange={(e) => setForm({ ...form, prazo: e.target.value })}
                  />
                </div>
                <Button variant="outline" disabled={salvando} onClick={salvarClassificacao}>
                  {salvando ? "Salvando..." : "Salvar classificação"}
                </Button>
              </div>
            </AdminCard>

            {tracking_raw ? (
              <AdminCard title="Texto cru da transportadora">
                <pre className="text-[11px] leading-relaxed text-slate-600 whitespace-pre-wrap break-all max-h-64 overflow-auto">
                  {typeof tracking_raw === "string"
                    ? tracking_raw
                    : JSON.stringify(tracking_raw, null, 2)}
                </pre>
              </AdminCard>
            ) : null}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminChamadoDetalhe;
